import {
  createLinkedDocumentationItem,
  getTargetVersionId,
  isAlreadyLinked,
  type LinkedDocumentationTarget,
} from 'shared/domain/issue/linkedDocumentation';
import { findVersionById } from 'shared/domain/documentation/currentVersion';
import { requestProjectApi } from 'serviceWorker/api/requestApi';
import { captureDataScope, DataOwner } from 'serviceWorker/helpers/dataScope';
import * as issuesDb from 'serviceWorker/db/issues/issues';
import { getOneByRemote as getDocumentationByRemote } from 'serviceWorker/db/documentations/documentations';
import {
  announceLinkedDocumentationChange,
  appendLinkedDocumentationToIssueRecord,
} from '../model';
import type {
  ImportFromDocumentationRequest,
  ImportFromDocumentationResultData,
} from 'shared/domain/issue/linkedDocumentation/operations';

function importFromDocumentationRequest(issueId: string, target: LinkedDocumentationTarget): Promise<void> {
  return requestProjectApi({ method: 'POST', path: `issue/${issueId}/importFromDocumentation`, body: target });
}

/** Keeps the new link on the device until the next sync brings it, unless the issue has it already. */
async function appendLinkedDocumentationToIssue(issueId: string, target: LinkedDocumentationTarget): Promise<void> {
  const issue = await issuesDb.getOne(issueId);
  if (!issue || isAlreadyLinked(issue.linkedDocumentation ?? [], target)) {
    return;
  }

  const documentation = await getDocumentationByRemote(target.documentationId);
  const versionId = getTargetVersionId(target);
  // A link to the documentation shows its current version: that one's rotation.
  const shownVersionId = versionId ?? documentation?.currentVersionId;
  const shownVersion = documentation && findVersionById(documentation, shownVersionId);

  await appendLinkedDocumentationToIssueRecord(
    issueId,
    createLinkedDocumentationItem({
      type: target.type,
      documentationId: target.documentationId,
      versionId,
      nameCandidates: [documentation?.name],
      imageRotationCandidates: [shownVersion?.imageRotation],
    })
  );
}

/** Links a documentation, or one of its versions, to the issue on the server and in IndexedDB; rejects when the server refused. */
export async function importFromDocumentation(
  detail: ImportFromDocumentationRequest
): Promise<ImportFromDocumentationResultData> {
  const { issueId, payload } = detail;
  const scope = captureDataScope({ owner: DataOwner.PROJECT });
  await importFromDocumentationRequest(issueId, payload);
  await scope.write(async () => {
    await appendLinkedDocumentationToIssue(issueId, payload);
    await announceLinkedDocumentationChange(issueId);
  });
  return payload;
}
